<template>
	<v-container class="fill-height" fluid>
		<v-row align="center" justify="center">
			<v-col cols="12" sm="8" md="6" lg="5">
				<v-card>
					<v-card-title class="primary white--text">
						One Time Secret
					</v-card-title>

					<v-card-text class="pt-8 text-center">
						<!-- Initial state: nothing is fetched until the user
						     clicks, so link-preview crawlers can't burn it. -->
						<div v-if="state === 'idle'">
							<v-icon size="64" color="primary"
								>lock_outline</v-icon
							>
							<p class="mt-4 subtitle-1">
								Someone shared a secret with you. It can be viewed
								<strong>only once</strong> and is destroyed the
								moment you reveal it.
							</p>
						</div>

						<!-- Revealed: show the decrypted secret. -->
						<div v-else-if="state === 'revealed'">
							<p
								class="subtitle-2 text-left mb-2 font-weight-medium grey--text text--darken-2"
							>
								Secret — copy it now, it has been destroyed:
							</p>
							<v-textarea
								:value="plaintext"
								outlined
								readonly
								auto-grow
								hide-details
								class="subtitle-2 text-left"
							>
								<template v-slot:append>
									<v-icon
										color="primary"
										tabindex="-1"
										@click="copyValue(plaintext)"
										>content_copy</v-icon
									>
								</template>
							</v-textarea>
							<v-alert dense text type="info" class="mb-0 mt-3">
								This secret has now been destroyed and cannot be
								viewed again.
							</v-alert>
						</div>

						<!-- Gone / error states. -->
						<div v-else-if="state === 'error'">
							<v-icon size="64" color="grey">block</v-icon>
							<p class="mt-4 subtitle-1">{{ errorMessage }}</p>
						</div>
					</v-card-text>

					<v-card-actions
						v-if="state === 'idle'"
						class="pb-6 px-6"
					>
						<v-spacer></v-spacer>
						<v-btn
							rounded
							color="primary"
							:loading="isRevealing"
							@click="reveal"
							>Reveal secret</v-btn
						>
						<v-spacer></v-spacer>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import axios from "axios";
import oneTimeSecretCrypto from "@/utils/oneTimeSecretCrypto.js";

export default {
	name: "secret-view",
	data() {
		return {
			state: "idle", // idle | revealed | error
			isRevealing: false,
			plaintext: "",
			errorMessage: "",
		};
	},
	created() {
		// The router runs in hash mode, so the whole route (incl. the key path
		// segment) lives in the URL fragment and is never sent to the server.
		this.secretId = this.$route.params.id;
		this.key = this.$route.params.key;
		if (!this.secretId || !this.key) {
			this.state = "error";
			this.errorMessage = "Invalid or incomplete secret link.";
		}
	},
	methods: {
		async reveal() {
			this.isRevealing = true;
			try {
				// Burn-after-reading: this request both fetches and destroys.
				const response = await axios.get(
					`${process.env.VUE_APP_APIURL}onetimesecret/${this.secretId}`
				);

				this.plaintext = await oneTimeSecretCrypto.decryptSecret({
					ciphertext: response.data.ciphertext,
					iv: response.data.iv,
					key: this.key,
				});
				this.state = "revealed";
			} catch (err) {
				this.state = "error";
				if (err.response && err.response.status === 404) {
					this.errorMessage =
						"This secret is no longer available (already viewed or expired).";
				} else if (err.response) {
					this.errorMessage =
						"Something went wrong. Please try again later.";
				} else {
					// Decryption failed: wrong, truncated or tampered link.
					this.errorMessage = "Invalid or corrupted link.";
				}
			} finally {
				this.isRevealing = false;
			}
		},

		async copyValue(value) {
			try {
				await navigator.clipboard.writeText(value);
				this.$store.dispatch("snackbar/showMessage", {
					color: "success",
					message: "Copied value",
				});
			} catch ($e) {
				this.$store.dispatch("snackbar/showMessage", {
					color: "error",
					message: "Error copying value",
				});
			}
		},
	},
};
</script>
